import { toast } from 'react-toastify';

import { applyComboPromotion, updateComboPromotion } from '@/routes/products';
import { IBestCombo, ICartCombo, IMainProduct, Item } from '@/store/ducks/combos/types';

import { IProduct } from '../../../../../store/ducks/products/types';

export interface ComboInfo {
  combo_percentage: number | undefined;
  combo_price: number;
  products_total: number;
}

export interface ProductWithQuantityLimits {
  id: number;
  min_quantity?: number;
  max_quantity?: number;
}

export interface FullProduct {
  id: number;
  [key: string]: any;
}

export const calculateComboPrice = (
  product: IProduct | { best_combo: IBestCombo } | undefined,
): number => {
  let total = 0;
  const percentage = product?.best_combo?.main_products[0]?.discount_percentage;

  if (product?.best_combo && percentage) {
    total = product.best_combo.main_products.reduce((acc, product) => {
      return acc + product.full_product.price * product.min_quantity;
    }, 0);

    total -= (total * percentage) / 100;
  }

  return total;
};

export const calculateProductsPrice = (
  product: IProduct | { best_combo: IBestCombo } | undefined,
): number => {
  if (!product?.best_combo) return 0;

  return product.best_combo.main_products.reduce((acc, product) => {
    return acc + product.full_product.price * product.min_quantity;
  }, 0);
};

export const getDiscountPrice = (price: number, discount_percentage: number): number => {
  return price - (price * (discount_percentage || 0)) / 100;
};

export const getComboInfo = (
  product: IProduct | { best_combo: IBestCombo } | undefined,
): ComboInfo => {
  return {
    combo_percentage: product?.best_combo?.main_products[0]?.discount_percentage,
    combo_price: calculateComboPrice(product),
    products_total: calculateProductsPrice(product),
  };
};

export const getQuantitySelectionText = (
  product: ProductWithQuantityLimits,
  variants: Record<string, FullProduct[]>,
  getTotalQuantityForProduct: (productId: number) => number,
): string => {
  const { min_quantity, max_quantity } = product;
  const hasVariants = (variants[product.id]?.length ?? 0) > 0;

  let text = 'Escolha ';

  // Handle quantity range display
  if (min_quantity && max_quantity && min_quantity !== max_quantity) {
    text += `de ${min_quantity} até ${max_quantity}`;
  } else if (max_quantity) {
    text += `${max_quantity}`;
  }

  // Handle singular/plural units
  text += max_quantity === 1 ? ' unidade' : ' unidades';

  // Add current selection count for variants
  if (hasVariants && max_quantity) {
    const currentTotal = getTotalQuantityForProduct(product.id);
    text += ` (Total: ${currentTotal}/${max_quantity})`;
  }

  return text;
};

export const getItemCountText = (count: number): string => {
  return `${count} ${count === 1 ? 'item' : 'itens'}`;
};

export interface ComboPromotionParams {
  quantities: Record<number, number>;
  combo_seq_id?: number;
  comboId: number;
  onClose: () => void;
  orderId?: number;
}

export interface ComboPromotionResult {
  success: boolean;
  error?: string;
  items?: string;
}

export const submitComboPromotion = async (
  params: ComboPromotionParams,
): Promise<ComboPromotionResult> => {
  const { quantities, comboId, onClose, orderId, combo_seq_id } = params;

  try {
    const full_products = Object.entries(quantities).map(([full_product_id, quantity]) => ({
      id: Number(full_product_id),
      quantity,
    }));

    let comboResponse = {} as any;

    if (combo_seq_id) {
      comboResponse = await updateComboPromotion(orderId, comboId, combo_seq_id, {
        full_products,
      });
    }

    if (!combo_seq_id) {
      comboResponse = await applyComboPromotion(orderId, {
        combos: [{ combo_id: comboId, full_products }],
      });
    }

    if ((comboResponse as any)?.message) {
      const errorMessage = (comboResponse as any)?.message || 'Erro ao aplicar promoção de combo.';
      toast.warn(errorMessage);
      return { success: false, error: errorMessage };
    }

    if (comboResponse) {
      const responseData = (comboResponse as any).data || comboResponse;

      if (responseData?.items && Array.isArray(responseData.items)) {
        const items = responseData.items
          .filter((item: Item) => item.combo_seq_id)
          .map((item: Item) => item.combo_seq_id);

        if (items.length > 0) {
          onClose();
          toast.success('Promoção de combo aplicada com sucesso!');
          return { success: true, items: items.join(',') };
        }
      }
    }

    return { success: false, error: 'Resposta inválida do servidor.' };
  } catch (error) {
    console.error('Error applying combo promotion:', error);
    const errorMessage = 'Erro inesperado ao aplicar promoção de combo.';
    return { success: false, error: errorMessage };
  }
};

export const formatToBestCombo = (rawComboData: ICartCombo | undefined): IBestCombo | undefined => {
  if (!rawComboData) return;
  const mainProducts: IMainProduct[] =
    rawComboData.combo_products?.map((comboProduct: any) => ({
      id: comboProduct.id,
      combo_id: comboProduct.combo_id,
      full_product_id: comboProduct.full_product_id,
      main_full_product_id: comboProduct.main_full_product_id,
      min_quantity: comboProduct.min_quantity,
      max_quantity: comboProduct.max_quantity,
      discount_percentage: comboProduct.discount_percentage,
      created_at: '',
      updated_at: '',
      variations_count: 0,
      full_product: {
        id: comboProduct.full_product.id,
        price: comboProduct.full_product.price,
        quantity: comboProduct.full_product.quantity,
        image: comboProduct.full_product.image,
        product_id: comboProduct.full_product.product_id,
        delivery_discount_data: null,
        product: {
          id: comboProduct.full_product.product_id,
          combo_id: comboProduct.combo_id,
          title: comboProduct.full_product.title,
          full_product_id: comboProduct.full_product_id,
          main_full_product_id: comboProduct.main_full_product_id,
          min_quantity: comboProduct.min_quantity,
          max_quantity: comboProduct.max_quantity,
          discount_percentage: comboProduct.discount_percentage,
          full_product: {
            title: comboProduct.full_product.title,
            brand: comboProduct.full_product.brand,
            measure: comboProduct.full_product.measure,
            category_name: comboProduct.full_product.category_name,
            id: comboProduct.full_product.id,
            ean: comboProduct.full_product.ean,
            price: comboProduct.full_product.price,
            quantity: comboProduct.full_product.quantity,
            product_id: comboProduct.full_product.product_id,
            image: comboProduct.full_product.image,
            shop_id: comboProduct.full_product.shop_id,
            external_code: comboProduct.full_product.external_code,
            see_as_unit: comboProduct.full_product.see_as_unit,
          },
        },
      },
    })) || [];

  // eslint-disable-next-line consistent-return
  return {
    id: rawComboData.id,
    company_id: rawComboData.company_id,
    shop_id: rawComboData.shop_id,
    name: rawComboData.name,
    is_enabled: rawComboData.is_enabled,
    start_date: rawComboData.start_date,
    end_date: rawComboData.end_date,
    external_code: rawComboData.external_code,
    image: rawComboData.image,
    image_key: rawComboData.image_key,
    observation: rawComboData.observation,
    created_at: rawComboData.created_at,
    updated_at: rawComboData.updated_at,
    main_products: mainProducts.filter(
      (product, index, array) =>
        array.findIndex((p) => p.main_full_product_id === product.main_full_product_id) === index,
    ),
  };
};

export const isInRange = ({
  combos,
  id,
  combo_id,
  quantity,
  action,
}: {
  combos: ICartCombo[];
  id: number;
  combo_id: number;
  quantity: number;
  action: 'add' | 'subtract';
}) => {
  const combo = combos.find((c) => c.id === combo_id);
  if (!combo) return false;
  const productInCombo = combo?.combo_products?.find((p) => p.main_full_product_id === id);

  if (!productInCombo) return false;

  if (action === 'subtract') return quantity - 1 > productInCombo.min_quantity;
  return quantity + 1 < productInCombo.max_quantity;
};
